import { batch } from 'react-redux';
import { EmeraldError, EmeraldErrorModal } from 'src/common/api/emerald-error';
import { popEmeraldModal } from 'src/common/emerald-modal-manager/emerald-modal-manager';
import { EJRecordTypes } from 'src/common/enumerations/ej-record-types';
import { EmeraldDialogTypes } from 'src/common/enumerations/emerald-dialog-types';
import { getEJItemBySequenceNumber } from 'src/common/helpers/item-list-helper';
import { getValueAsNumber } from 'src/common/helpers/number-helper';
import { MessageModalSizeEnum } from 'src/common/interfaces/dtos/IMessageModal';
import { store } from 'src/common/redux/configure-store';
import { TenderMenuActions } from 'src/common/redux/reducers/tender-menu-reducer';
import { TenderingActions } from 'src/common/redux/reducers/tendering-reducer';
import { TransactionActions } from 'src/common/redux/reducers/transaction-reducer';
import { cancelLine } from 'src/home-page/business-logic/cancel-line-or-item';
import { cancelTenderPayment } from 'src/home-page/business-logic/cancel-tender-payment';
import {
  updateCartStatus,
  updateTransactionStatus,
} from 'src/home-page/business-logic/update-cart-status';
import { voidActiveTransaction } from 'src/home-page/business-logic/void-transaction';
import { CartStatus } from 'src/items-list/business-logic/service/enumerations/cart-status';
import { popupMessage } from 'src/messages/business-logic/popup-message';
import { removeTenderingView } from './../../tendering/business-logic/tendering-actions';

const modalId = 'AuthorizationDeclinedModal';

const voidItem = async (lineId: string): Promise<void> => {
  const declinedItem = getEJItemBySequenceNumber(getValueAsNumber(lineId));
  if (declinedItem) {
    await cancelLine(declinedItem);
  }
};

// TODO: remove this voidBugOverride condition when VoidFinalization bug is fixed in Selling Engine
const voidOnlineItem = async (lineId: string, voidBugOverride: boolean = false): Promise<void> => {
  const itemCount = store.getState().itemList.itemCount;
  const tenderList = store
    .getState()
    .itemList.ejItems.filter(item => item.Type === EJRecordTypes.Tender);

  if (tenderList.length === 0 && itemCount <= 1) {
    voidBugOverride && (await voidItem(lineId));
    await voidActiveTransaction();
  } else {
    await voidItem(lineId);
  }
};

const voidItemAndBackToItemization = async (error: EmeraldError): Promise<void> => {
  batch((): void => {
    store.dispatch(TenderMenuActions.setSelectedTender());
    store.dispatch(TenderingActions.setIsTendering(false));
    store.dispatch(TenderingActions.setPaymentInfoText(''));
  });
  await updateCartStatus();
  const { unPaidPreTender } = store.getState().tenderMenu;
  if (unPaidPreTender?.lineId) {
    await cancelTenderPayment(unPaidPreTender.lineId);
  }
  await voidItem(error.data[0].restrictedLines[0]);
  store.dispatch(TenderMenuActions.clearUnPaidPreTender());
  const itemCount = store.getState().itemList.itemCount;
  if (itemCount === 0) {
    await voidActiveTransaction();
    await removeTenderingView();
    store.dispatch(TransactionActions.setIsTransactionalTotaled(false));
  }
  // back to tendering
  else if (store.getState().itemList.amountDue > 0) {
    await updateTransactionStatus(CartStatus.Total);
    await updateTransactionStatus(CartStatus.Tender);
  } else {
    await removeTenderingView();
    store.dispatch(TransactionActions.setIsTransactionalTotaled(false));
  }
};

export const handleOnlineItemDeclinedOnTender = async (error: EmeraldError): Promise<void> =>
  new Promise(resolve => {
    const onAccept = () =>
      voidItemAndBackToItemization(error)
        .catch(popupMessage)
        .finally(() => {
          popEmeraldModal(modalId);
          resolve();
        });

    showOnlineItemDeclineError(
      'AuthorizationDeclined',
      'AuthorizationDeclined_Transaction',
      onAccept,
    );
  });

export const handleOnlineItemDeclinedOnCheckout = async (lineId: string): Promise<void> =>
  new Promise(resolve => {
    const onAccept = () =>
      voidOnlineItem(lineId, true)
        .catch(popupMessage)
        .finally(() => {
          popEmeraldModal(modalId);
          resolve();
        });

    showOnlineItemDeclineError('AuthorizationFailed', 'ActivationFailed_ItemVoid', onAccept);
  });

export const handleOnlineItemDeclinedOnItemSale = () => {
  throw onlineItemDeclineErrorModal('AuthorizationFailed', 'AuthorizationFailed_ItemNotAdded');
};

const showOnlineItemDeclineError = (
  message: string,
  subMessage: string,
  onAccept: () => Promise<void>,
) => {
  const messageModel = onlineItemDeclineErrorModal(message, subMessage, onAccept);
  popupMessage(messageModel);
};

const onlineItemDeclineErrorModal = (
  message: string,
  subMessage: string,
  onAccept?: () => Promise<void>,
) =>
  new EmeraldErrorModal({
    title: 'OnlineItem',
    message,
    subMessage,
    type: EmeraldDialogTypes.Info,
    size: MessageModalSizeEnum.Medium,
    dialogTestIdPrefix: 'online-item-declined',
    onAccept,
    acceptButtonLabel: 'Ok',
    hideCancelButton: true,
    disableBackdropClick: true,
    modalId: modalId,
    dialogHeight: 49.479,
  });
